import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Divider, Flex, Result, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { useMutation } from '@tanstack/react-query';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import {
  StoreCommunicationMessageLogActionSlug,
  StoreCommunicationMessageLogOrigin
} from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

function getTypeMessage(type: string | null) {
  if (type === 'billingReminder' || type === 'billingBankSlipReminder') {
    return (
      <>
        relacionadas a <strong>lembretes de vencimento de faturas de assinatura</strong>
      </>
    );
  }

  if (type === 'lateInvoice') {
    return (
      <>
        relacionadas a <strong>atraso de faturas de assinatura</strong>
      </>
    );
  }

  return null;
}

export function UnsubscribeWhatsappMessage() {
  const [unsubscribed, setUnsubscribed] = useState(false);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const sentMessageId = params.get('sentMessageId');
  const type = params.get('type');
  const blacklistMessage = useMutation({
    mutationFn: (sentMessageId: string) => subscriptionsService.blacklistMessage(sentMessageId),
    onSuccess: () => {
      setUnsubscribed(true);
      storeCommunicationMessageLog.mutate(sentMessageId);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.data?.message ||
        'Erro ao processar o descadastro. Por favor, tente novamente mais tarde.';
      toast.error(message);
    }
  });
  const storeCommunicationMessageLog = useMutation({
    mutationFn: (sentMessageId: string) =>
      subscriptionsService.storeCommunicationMessageLog({
        id: sentMessageId,
        actionSlug: StoreCommunicationMessageLogActionSlug.CLICKED,
        origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
        context: 'Descadastro lembrete de vencimento de fatura',
        createdAt: new Date().toISOString()
      }),
    onError: () => {
      toast.error('Erro ao processar o descadastro. Por favor, tente novamente mais tarde.');
    }
  });

  const typeMessage = getTypeMessage(type);

  if (!sentMessageId || !typeMessage) {
    return (
      <UnloggedLayout>
        <div className='mx-auto flex max-w-120 flex-col items-center justify-center px-4 pt-16 pb-8'>
          <Result
            status='error'
            title='Link inválido'
            subTitle='Parâmetros inválidos. Por favor, verifique o link recebido.'
          />
        </div>
      </UnloggedLayout>
    );
  }

  return (
    <UnloggedLayout>
      <div className='mx-auto flex max-w-120 flex-col items-center justify-center px-4 pt-16 pb-8'>
        {unsubscribed ? (
          <Result
            status='success'
            title='Descadastro realizado com sucesso!'
            subTitle={<>Você não receberá mais mensagens {typeMessage} no WhatsApp.</>}
          />
        ) : (
          <Result
            status='warning'
            title='Você está quase lá!'
            subTitle={
              <Flex vertical gap='small'>
                <Typography.Text>
                  Ao confirmar, você deixará de receber mensagens no WhatsApp {typeMessage} do MyEduzz.
                </Typography.Text>
                <Typography.Text>
                  Este processo é irreversível e não será possível se cadastrar novamente no futuro.
                </Typography.Text>
              </Flex>
            }
            extra={
              <Button
                icon={<Icon.Bell  />}
                type='primary'
                onClick={() => blacklistMessage.mutate(sentMessageId)}
                disabled={blacklistMessage.isPending}
                loading={blacklistMessage.isPending}
              >
                Confirmar descadastro
              </Button>
            }
          />
        )}

        <Flex className='mt-8' align='center' justify='center'>
          <Typography.Link href='https://www.eduzz.com/privacidade' target='_blank' rel='noopener noreferrer'>
            Política de privacidade
          </Typography.Link>
          <Divider type='vertical' />
          <Typography.Link href='https://www.eduzz.com/termos' target='_blank' rel='noopener noreferrer'>
            Termos de compra
          </Typography.Link>
          <Divider type='vertical' />
          <Typography.Link
            href='https://ajuda.eduzz.com/hc/pt-br/articles/1260806909249-Como-alterar-a-forma-de-pagamento-de-uma-assinatura'
            target='_blank'
            rel='noopener noreferrer'
          >
            Falar com suporte
          </Typography.Link>
        </Flex>
      </div>
    </UnloggedLayout>
  );
}
